import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ActivityIndicator, TextStyle, View, ViewStyle } from 'react-native'

import { iconCrown } from '../../assets'
import { color } from '../../theme'
import { Avatar } from '../avatar'
import { SvgIcon } from '../svg-icon'
import { Text } from '../text'

import { numberWithOrdinalSuffix, useStores } from '@trivie/core'
import { Trans } from 'react-i18next'
import LinearGradient from 'react-native-linear-gradient'
import { ListChildComponentProps, VariableSizeList } from 'react-window'

import AutoSizer from 'react-virtualized-auto-sizer'

import { useMediaQuery } from '@mui/material'

export const Leaderboard = observer((props: any) => {
  const { userPoints, userRank, contest = false, loading, filter, selectedTag, data } = props
  const { userStore } = useStores()
  const { user } = userStore

  const isMobile = !useMediaQuery('(min-width:800px)')

  const [currentUserRank, setCurrentUserRank] = useState<any>(null)

  useEffect(() => {
    if (user) {
      if (userPoints || userRank) {
        setCurrentUserRank({
          rank: userRank,
          previous_rank: null,
          current_rank: userRank,
          points: userPoints,
        })
      } else {
        const item = data?.filter((f) => f.user_id === user.id)
        setCurrentUserRank(item?.[0])
      }
    }
  }, [user, data])

  const renderLeaderboardHeader = () => {
    const IsBottomPadding =
      currentUserRank &&
      (currentUserRank?.previous_rank === null ||
        currentUserRank?.previous_rank === currentUserRank?.current_rank ||
        currentUserRank?.previous_rank === currentUserRank?.rank)

    const USER_RANK_CONTAINER_STYLE: React.CSSProperties = {
      ...USER_RANK_CONTAINER,
      marginBottom: 20,
    }

    const HEADER_CONTAINER_STYLE: ViewStyle = isMobile ? HEADER_CONTAINER_MOBILE : HEADER_CONTAINER
    const USER_POINTS_CONTAINER_STYLE: ViewStyle = isMobile
      ? USER_POINTS_CONTAINER_MOBILE
      : USER_POINTS_CONTAINER

    return user?.exclude_from_leaderboard ? (
      <div style={{ marginTop: 12 }} />
    ) : (
      <>
        <div style={USER_RANK_CONTAINER_STYLE}>
          <LinearGradient
            useAngle
            angle={95.61}
            locations={[0.0627, 0.8]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            colors={['#E4EBFF', '#ECE9FF']}
            style={RANK_GRADIENT}
          >
            <View style={{ alignSelf: 'center' }}>
              {(selectedTag === '' || selectedTag === 'User') && (
                <Avatar
                  source={user?.photo_url}
                  name={`${user?.first_name} ${user?.last_name}`}
                  size="medium"
                />
              )}
            </View>

            <View style={USER_POINTS_CONTAINER_STYLE}>
              <View style={USER_EARNED_CONTAINER}>
                <View style={HEADER_CONTAINER_STYLE}>
                  <Trans
                    i18nKey={
                      filter === 'daily'
                        ? 'Leaderboard.earnedTodaySubtitle'
                        : filter === 'weekly'
                          ? 'Leaderboard.earnedWeeklySubtitle'
                          : filter === 'monthly'
                            ? 'Leaderboard.earnedMonthlyTimeSubtitle'
                            : 'Leaderboard.earnedAllTimeSubtitle'
                    }
                    count={currentUserRank?.points}
                    style={{ flexWrap: 'wrap' }}
                    values={{ count: currentUserRank?.points || 0 }}
                  >
                    <Text numberOfLines={2} align="center" variant="label" />
                    <Text align="center" variant="labelEmphasized" />
                  </Trans>
                </View>
              </View>
            </View>
            <View
              style={{
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {currentUserRank?.current_rank ||
                (currentUserRank?.rank && (
                  <View style={{ flexDirection: 'row' }}>
                    <Text
                      variant="labelEmphasized"
                      style={{
                        ...CURRENT_RANK_TEXT,
                        bottom: IsBottomPadding ? 0 : -13,
                      }}
                      text={numberWithOrdinalSuffix(
                        (currentUserRank?.current_rank || currentUserRank?.rank) ?? 0,
                      )}
                    />
                  </View>
                ))}
            </View>
          </LinearGradient>
        </div>
      </>
    )
  }

  const renderTopThree = (item: any, index: any) => {
    return (
      <View style={TOP_THREE_CONTENT}>
        {(index === 1 || index === 2) && (
          <View style={{ position: 'absolute', top: -40 }}>
            <Text
              variant="body2Emphasized"
              style={{ textAlign: 'center', top: 8 }}
              text={`${numberWithOrdinalSuffix(item.rank)}`}
            />
          </View>
        )}
        {index === 0 && (
          <View
            style={{
              position: 'absolute',
              marginTop: isMobile ? -130 : -90,
              zIndex: 999,
            }}
          >
            <SvgIcon size={50} src={iconCrown} />
          </View>
        )}
        <Avatar
          size={index === 0 ? 'large' : 'medium60'}
          source={item?.photo_url}
          name={`${item.first_name} ${item.last_name}`}
        />
        <View style={{ position: 'absolute', top: index === 0 ? 100 : 60 }}>
          <Text
            variant="body2Emphasized"
            style={USER_TOP_NAME}
            text={`${item.first_name} ${item.last_name}`}
          />
          <Text variant="body2Medium" style={ITEM_POINTS} text={`${item.points}`} />
        </View>
      </View>
    )
  }

  if (data) {
    const tie = data[0]?.rank === data[1]?.rank

    return loading ? (
      <View style={LOADER}>
        <ActivityIndicator color="#999" size="small" />
      </View>
    ) : (
      <div style={{ height: '100%' }}>
        {contest || selectedTag === '' || selectedTag === 'User' ? renderLeaderboardHeader() : null}
        <div
          style={{
            height: '100%',
            paddingTop: 0,
            alignSelf: 'center',
            display: 'flex',
          }}
        >
          <VList
            tie={tie}
            isMobile={isMobile}
            contest={contest}
            data={data}
            selectedTag={selectedTag}
            renderTopThree={renderTopThree}
          />
        </div>
      </div>
    )
  } else {
    return loading ? (
      <View style={LOADER}>
        <ActivityIndicator color="#999" size="small" />
      </View>
    ) : null
  }
})

const VList = (props: any) => {
  const { data, tie, contest, selectedTag, isMobile, renderTopThree } = props
  const isRenderTopThree =
    data?.length > 3 && (selectedTag === '' || selectedTag === 'User' || contest) && !tie

  const getItemSize = (index) => {
    if (isRenderTopThree && index <= 2) {
      if (index === 0) return 120
      return 30 //give
    }
    return 100
  }

  const Row = ({ index, style }) => {
    if (isRenderTopThree && index <= 2) {
      if (index == 0)
        return (
          <View style={{ ...TOP_THREE_CONTAINER, marginBottom: 20, marginTop: 20 }}>
            {renderTopThree(data[1], 1)}
            {renderTopThree(data[0], 0)}
            {renderTopThree(data[2], 2)}
          </View>
        )
      return <></>
    } else {
      return (
        <>
          <div>
            {(contest
              ? renderUser
              : selectedTag !== '' && selectedTag !== 'User'
                ? renderTag
                : renderUser)({ data, style, index })}
          </div>
        </>
      )
    }
  }
  return (
    <AutoSizer>
      {({ height, width }) => (
        <VariableSizeList
          height={height}
          itemCount={data?.length}
          itemSize={getItemSize}
          width={width}
        >
          {Row}
        </VariableSizeList>
      )}
    </AutoSizer>
  )
}

function renderTag(props: ListChildComponentProps) {
  const { data, index, style } = props
  const item = data[index]
  return (
    <View key={index} style={{ ...style, padding: 0 }}>
      <View
        style={{
          backgroundColor: color.blueGrey,
          ...LEADERBOARD_ITEM,
          borderRightWidth: item.contains_current_user ? 8 : 0,
          borderRightColor: item.contains_current_user ? color.blue : color.transparent,
          marginBottom: 12,
        }}
      >
        <View style={ITEM_CONTAINER}>
          <View
            style={{
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <Text variant="body1Emphasized" text={item.tag_value} />
            <Text
              variant="body2Emphasized"
              style={POINTS}
              tx="Leaderboard.points"
              txOptions={{ count: Math.round(item.points) }}
            />
          </View>
        </View>
        {item.rank && (
          <View style={{ ...RANK_CONTAINER, left: item.contains_current_user ? 8 : 0 }}>
            <Text variant="labelEmphasized" text={numberWithOrdinalSuffix(item.rank)} />
          </View>
        )}
      </View>
    </View>
  )
}

function renderUser(props: ListChildComponentProps) {
  const { data, index, style } = props
  const item = data[index]
  return (
    <>
      <View key={index} style={{ ...style, padding: 0 }}>
        <View style={{ backgroundColor: color.blueGrey, ...LEADERBOARD_ITEM, marginBottom: 12 }}>
          <View style={ITEM_CONTAINER}>
            <View style={ITEM_CONTENT}>
              <Avatar
                source={item?.photo_url}
                name={`${item.first_name} ${item.last_name}`}
                size="small40"
              />
            </View>
            <View style={USER_TEXT_CONTAINER}>
              <Text
                variant="body1Emphasized"
                text={
                  item.first_name == null || item.last_name == 'null'
                    ? 'Unnamed User'
                    : `${item.first_name} ${item.last_name}`
                }
              />
              <Text
                style={POINTS}
                tx="Leaderboard.points"
                variant="body2Emphasized"
                txOptions={{ count: Math.round(item.points) }}
              />
            </View>
          </View>
          <View style={RANK_CONTAINER}>
            <Text variant="labelEmphasized" text={numberWithOrdinalSuffix(item.rank)} />
          </View>
        </View>
      </View>
    </>
  )
}

const HEADER_CONTAINER: ViewStyle = {
  flex: 1,
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  paddingTop: 0,
}

const HEADER_CONTAINER_MOBILE: ViewStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  flexDirection: 'row',
}

const LOADER: ViewStyle = { flex: 1, flexDirection: 'column', justifyContent: 'center' }
const TOP_THREE_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  marginBottom: 72,
  justifyContent: 'space-around',
  alignItems: 'center',
}
const TOP_THREE_CONTENT: ViewStyle = {
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-around',
}
const LEADERBOARD_ITEM: ViewStyle = {
  borderRadius: 16,
  minHeight: 68,
  paddingVertical: 14,
  width: '100%',
  display: 'flex',
  flex: 1,
  paddingHorizontal: 16,
  flexDirection: 'row',
  justifyContent: 'space-between',
  paddingRight: 16,
  marginBottom: 20,
}
const ITEM_CONTAINER: ViewStyle = {
  flex: 1,
  width: '100%',
  flexDirection: 'row',
  height: '100%',
  justifyContent: 'flex-start',
}
const ITEM_CONTENT: ViewStyle = {
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
}
const RANK_CONTAINER: ViewStyle = {
  flex: 1,
  width: '100%',
  justifyContent: 'flex-end',
  flexDirection: 'row',
  alignItems: 'center',
  display: 'flex',
}
const POINTS: TextStyle = { color: color.shade30 }
const RANK_GRADIENT: ViewStyle = {
  borderRadius: 16,
  height: 80,
  width: '100%',
  paddingHorizontal: 16,
  flexDirection: 'row',
  justifyContent: 'space-between',
}
const USER_RANK_CONTAINER: React.CSSProperties = {
  height: 80,
  flexDirection: 'row',
  marginTop: 12,
  width: '100%',
  marginBottom: 20,
}
const USER_TOP_NAME: TextStyle = {
  width: 100,
  marginTop: 10,
  textAlign: 'center',
}
const CURRENT_RANK_TEXT: TextStyle = { alignSelf: 'flex-start', alignContent: 'center' }
const USER_POINTS_CONTAINER: ViewStyle = {
  width: '100%',
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  justifyContent: 'space-between',
  paddingRight: 10,
  alignItems: 'center',
}
const USER_POINTS_CONTAINER_MOBILE: ViewStyle = {
  flex: 1,
  justifyContent: 'center',
}
const USER_EARNED_CONTAINER: ViewStyle = {
  flexDirection: 'column',
  justifyContent: 'center',
  paddingHorizontal: 16,
}
const USER_TEXT_CONTAINER: TextStyle = {
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  left: 12,
}
const ITEM_POINTS: TextStyle = {
  color: color.shade30,
  textAlign: 'center',
}
